import React from 'react';
import { ICompany, TProjects } from 'store/companies/companiesTypes';
import styled from 'styled-components';
import messages from './Messages';
import { black, blue, green, orange, red, selected } from 'utils/style/colors';
import { IOption } from 'components/Select/Select';
import SelectWithPopover from '../../../components/Select/SelectWithPopover';
import { HiddenFieldValue, IField, ITask } from 'store/tasks/tasksType';
import LocalIcon from 'ui/LocalIcon';
import HorizontalCollapsible from '../../../components/Collapsible/HorizontalCollapsible';
import UserMixpanel from '../../../utils/mixpanel/MixpanelClass';
import { convertDigitToProperFormat } from 'utils/number';
// import { removeDuplicates } from '../../../utils/happyFunctions';

export interface ITemplateTaskCounts {
  completedTaskCountMain: number;
  inProgressTaskCountMain: number;
  notStartedTaskCountMain: number;
  total: number;
}

interface IComponentProps {
  loading?: boolean;
  showAll?: boolean;
  projects: TProjects;
  projectId?: number;
  showCategorized?: boolean;
  onSelect: (
    selectedTemplateId: 'all' | number,
    fields: { [key: string]: string }
  ) => () => void;
  // templates: Array<{
  //   id: number;
  //   title: ITask['taskName'];
  //   fields: ITask['fields'];
  // }>;
  fieldValues?: { [key: string]: string };
  boardField?: IField;
  showBoard?: (fieldName?: IField) => any;
  selectedTemplate: number | 'all';
  tasks: { [p: number]: ITask };
  company: ICompany;
  isShowSummery?: boolean;
  taskCounts?: ITemplateTaskCounts | null;
}

interface ITemplateSpanProps {
  isActive: boolean;
  isShowSummery?: boolean;
}

const HorizontalList = styled.div`
  width: fit-content;
  overflow: auto;
  white-space: nowrap;
  display: flex;
  align-items: center;
`;
const FieldList = styled.div`
  white-space: nowrap;
  display: flex;
  align-items: center;
`;
const TemplateSpan = styled.span<ITemplateSpanProps>`
  color: ${(props) => (props.isActive ? 'var(--exact-white)' : black)};
  border: 1px solid ${selected};
  cursor: pointer;
  margin: 0 0.5rem;
  display: inline-block;
  padding: ${(props) =>
    props.isActive && !props.isShowSummery ? '0.2rem 0.5rem' : '0.75rem'};
  border-radius: 10rem;
  background: ${(props) => (props.isActive ? selected : 'var(--exact-white)')};
`;

const TemplateContainer = styled.span`
  display: flex;
  align-items: center;
  overflow: hidden;
`;

const CountBadges = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-inline-start: 0.35rem;
  vertical-align: middle;
`;

const CountBadge = styled.span<{ $bg: string; $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  min-width: 1.25rem;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  font-size: 0.7rem;
  line-height: 1.2;
  font-weight: 600;
  background: ${(props) => props.$bg};
  color: var(--exact-white);
  opacity: ${(props) => (props.$active ? 1 : 0.95)};
  white-space: nowrap;
`;

const CountBadgeLabel = styled.span`
  font-weight: 500;
  opacity: 0.9;
`;

const CountBadgeValue = styled.span`
  font-weight: 700;
`;

function renderTaskCountBadges(
  taskCounts: ITemplateTaskCounts,
  isActive: boolean
) {
  const badges: Array<{
    bg: string;
    label: string;
    value: number;
  }> = [
    {
      bg: blue,
      label: messages.totalTasks,
      value: taskCounts.total,
    },
    {
      bg: green,
      label: messages.complete,
      value: taskCounts.completedTaskCountMain,
    },
    {
      bg: orange,
      label: messages.started,
      value: taskCounts.inProgressTaskCountMain,
    },
    {
      bg: red,
      label: messages.notStarted,
      value: taskCounts.notStartedTaskCountMain,
    },
  ];

  return (
    <CountBadges onClick={(e: React.MouseEvent) => e.stopPropagation()}>
      {badges.map((badge) => (
        <CountBadge key={badge.label} $bg={badge.bg} $active={isActive}>
          <CountBadgeLabel>{badge.label}</CountBadgeLabel>
          <CountBadgeValue>
            {convertDigitToProperFormat(String(badge.value))}
          </CountBadgeValue>
        </CountBadge>
      ))}
    </CountBadges>
  );
}

export function getFieldOptions(
  field: IField,
  projects: TProjects,
  projectId: number | undefined,
  selectedTemplate: number | string
) {
  const options: IOption[] = [
    // { value: '', label: field.name + ' : ' + messages.all },
  ];
  let hidden = false;
  if (field.options)
    Object.values(field.options!)
      .map((fieldOption) => ({
        value: fieldOption,
        label: fieldOption,
      }))
      .forEach((i) => options.push(i));
  Object.values(projects)
    .filter((it) => !projectId || it.id === projectId)
    .forEach((project) => {
      const projectTemplate =
        project.templates &&
        project.templates.find((item) => item.taskId === selectedTemplate);
      if (projectTemplate) {
        if (
          projectTemplate.overrides?.fields?.[field.name] ===
            HiddenFieldValue &&
          projectId
        ) {
          hidden = true;
        }
        (projectTemplate.overrides?.fieldOptions?.[field.name] || []).forEach(
          (value: string) => {
            options.push({
              value: value,
              label: value,
            });
          }
        );
      }
    });
  return {
    options: options
      .filter(
        (value, index, arr) =>
          index === arr.findIndex((y) => y.value === value.value)
      )
      .sort((a, b) => {
        if (a.label < b.label) {
          return -1;
        }
        if (a.label > b.label) {
          return 1;
        }
        return 0;
      }),
    hidden,
  };
}

const TemplateSelectorList: React.FC<IComponentProps> = ({
  showAll = true,
  // showCategorized = true,
  onSelect,
  selectedTemplate,
  fieldValues,
  projectId,
  projects,
  showBoard,
  boardField,
  company,
  tasks,
  isShowSummery = false,
  taskCounts,
}) => {
  const dfs = (compTemp: any, projTemp: number[]): number[] => {
    const stack: number[] = [];
    projTemp.forEach((tempId) => {
      if (!stack.includes(tempId)) stack.push(tempId);
      compTemp[tempId]?.children?.forEach((childTemp: any) => {
        if (!stack.includes(childTemp)) {
          stack.push(childTemp);
        }
      });
    });
    if (stack.length === projTemp.length) {
      return stack;
    }
    return dfs(compTemp, stack);
  };

  const getTemplateIds = (projectId?: number) => {
    let templatesIds: number[] = [];
    const companyTemplates = company?.templates;
    if (projectId) {
      const projectTemplatesIds = projects[projectId]?.templates || [];
      templatesIds = companyTemplates
        ? dfs(companyTemplates, [
            ...new Set(
              projectTemplatesIds
                .map((v: any) => (v.allowRoot ? v.taskId : 0))
                .filter((it: any) => it !== 0)
            ),
          ])
        : [];
    } else if (companyTemplates) {
      const allProjectRootTemplateIds = [] as any[];
      Object.values(projects || {}).forEach((it) => {
        if (it.companyId !== company.id) return;
        it?.templates
          ?.map((v) => (v.allowRoot ? v.taskId : 0))
          .filter((it: any) => it !== 0)
          .forEach((template: any) => {
            if (!allProjectRootTemplateIds.includes(template))
              allProjectRootTemplateIds.push(template);
          });
      });
      templatesIds = dfs(companyTemplates, allProjectRootTemplateIds);
    }
    templatesIds = templatesIds.filter((id) => {
      return !tasks[id]?.archived;
    });
    return templatesIds.reduce(
      (
        temps: Array<{
          id: number;
          title: string;
          fields: ITask['fields'];
        }>,
        id
      ) => {
        temps.push({
          id,
          title: tasks[id]?.taskName,
          fields: tasks[id]?.fields,
        });
        return temps;
      },
      []
    );
  };

  const handleSelectFieldChange = (
    field: IField,
    value: string | undefined
  ) => {
    const nextFieldValues = { ...(fieldValues || {}) };
    if (!value) delete nextFieldValues[field.name];
    else nextFieldValues[field.name] = value;

    onSelect(selectedTemplate, nextFieldValues)();
  };

  const mx = UserMixpanel.getInstance();
  const templates = getTemplateIds(projectId);
  const selectedTemplateObj =
    selectedTemplate && templates.find((t) => t.id === selectedTemplate);
  let selectableFields = [] as IField[];
  if (selectedTemplateObj) {
    selectableFields = selectedTemplateObj.fields
      .filter((it) => {
        return (
          (it.options && Object.keys(it.options).length > 0) ||
          Object.values(projects).find((project) => {
            return (
              ((!projectId || project.id === projectId) &&
                (
                  project?.templates?.find(
                    (item) => item.taskId === selectedTemplate
                  )?.overrides?.fieldOptions?.[it.name] || []
                )?.length > 0) ||
              false
            );
          })
        );
      })
      .reduce((list: IField[], it) => {
        list.push(it);
        return list;
      }, selectableFields);
  }

  return (
    <div style={{ overflowX: 'auto', flexShrink: 0 }}>
      {!isShowSummery ? (
        templates.length >= 1 && (
          <HorizontalList>
            {showAll && templates.length > 1 && (
              <TemplateSpan
                isActive={selectedTemplate === 'all'}
                onClick={onSelect('all', {})}
              >
                <div
                  style={
                    selectedTemplate === 'all' ? { padding: '0.5rem' } : {}
                  }
                >
                  {messages.all}
                </div>
              </TemplateSpan>
            )}
            {templates.map((temp) => (
              <TemplateContainer key={temp.id}>
                <HorizontalCollapsible
                  header={
                    <TemplateSpan
                      isActive={selectedTemplate === temp.id}
                      onClick={onSelect(temp.id, {})}
                      isShowSummery={isShowSummery}
                    >
                      {temp.title}
                      {selectedTemplate === temp.id &&
                        !!tasks[temp.id]?.taskProgressType?.main &&
                        taskCounts &&
                        renderTaskCountBadges(taskCounts, true)}
                    </TemplateSpan>
                  }
                  isOpen={selectedTemplate === temp.id}
                >
                  {selectableFields.length > 0 ? (
                    <FieldList>
                      {selectableFields.map((field, index) => {
                        const { options, hidden } = getFieldOptions(
                          field,
                          projects,
                          projectId,
                          selectedTemplate
                        );
                        if (hidden) return <></>;
                        return (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              padding: '0 0.5rem',
                              gap: '0.5rem',
                            }}
                          >
                            <div
                              key={index}
                              style={{
                                display: 'inline-block',
                              }}
                            >
                              <SelectWithPopover
                                inputStyle={{
                                  margin: 0,
                                  padding: 0,
                                  paddingInlineStart: '0.25rem',
                                }}
                                fixMisPositionError={true}
                                onChangeValues={(fieldOption) => {
                                  handleSelectFieldChange(
                                    field,
                                    fieldOption.length > 0
                                      ? fieldOption[0].value.toString()
                                      : undefined
                                  );
                                }}
                                placeholder={field.title}
                                multiChoice={false}
                                isRequired={false}
                                round={true}
                                values={
                                  fieldValues?.[field.name]
                                    ? [
                                        {
                                          value: fieldValues?.[field.name],
                                          label: fieldValues?.[field.name]
                                            ? fieldValues?.[field.name]
                                            : field.name + ' : ' + messages.all,
                                        },
                                      ]
                                    : []
                                }
                                options={options}
                                placement="auto"
                                arrow={false}
                              />
                            </div>
                            {showBoard ? (
                              <LocalIcon
                                iconName={'trello.svg'}
                                color={'var(--exact-white)'}
                                onClick={() => {
                                  mx.sendVistedTaskInBoardView(field.name);
                                  showBoard(
                                    boardField?.name === field.name
                                      ? undefined
                                      : field
                                  );
                                }}
                                style={{
                                  padding: '7px',
                                  cursor: 'pointer',
                                  flexShrink: '0',
                                }}
                              />
                            ) : undefined}
                          </div>
                        );
                      })}
                    </FieldList>
                  ) : (
                    <></>
                  )}
                </HorizontalCollapsible>
              </TemplateContainer>
            ))}
          </HorizontalList>
        )
      ) : (
        <>
          <TemplateSpan
            isActive={selectedTemplate === 'all'}
            onClick={onSelect('all', {})}
          >
            <div
              style={selectedTemplate === 'all' ? { padding: '0.5rem' } : {}}
            >
              {messages.all}
            </div>
          </TemplateSpan>
          {templates.map((temp) => (
            <TemplateSpan
              key={`${Math.random()}-${temp.id}`}
              isActive={selectedTemplate === temp.id}
              onClick={onSelect(temp.id, {})}
              isShowSummery={isShowSummery}
            >
              {temp.title}
            </TemplateSpan>
          ))}
        </>
      )}
    </div>
  );
};

export default React.memo(TemplateSelectorList);
